import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import {
  MessageCircle, Send, User, Bot, Phone, X, RefreshCw, Hand, Bell,
} from 'lucide-react';
import { QuickRepliesPopover } from './QuickRepliesPopover';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 4000;

const formatRelative = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

export const LiveChatsTab = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [convo, setConvo] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [filterAll, setFilterAll] = useState(true); // true=all, false=mine
  const lastNotifiedAtRef = useRef(0);

  const refreshSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/sessions`, {
        params: filterAll ? {} : { mine: true },
      });
      const next = res.data?.sessions || [];
      // Browser notification + sound when a NEW unread session appears
      const newest = next.find((s) => s.unread_for_dispatcher);
      if (newest && newest.last_message_at) {
        const tms = new Date(newest.last_message_at).getTime();
        if (tms > lastNotifiedAtRef.current && lastNotifiedAtRef.current !== 0) {
          fireNotification(newest);
        }
        lastNotifiedAtRef.current = Math.max(lastNotifiedAtRef.current, tms);
      } else if (lastNotifiedAtRef.current === 0 && next.length > 0) {
        // Seed timestamp on first load so we don't ping for historical messages
        lastNotifiedAtRef.current = Date.now();
      }
      setSessions(next);
    } catch (err) { console.warn('[live-chats] refresh failed:', err); }
  }, [filterAll]);

  useEffect(() => {
    refreshSessions();
    const id = setInterval(refreshSessions, POLL_INTERVAL_MS);
    requestNotificationPermission();
    return () => clearInterval(id);
  }, [refreshSessions]);

  // If the ChatNotificationBell handed us a session id, auto-open it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const target = window.sessionStorage.getItem('live-chats-open-session');
    if (target) {
      window.sessionStorage.removeItem('live-chats-open-session');
      openSession(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSession = async (sessionId) => {
    setActiveId(sessionId);
    setConvo(null);
    try {
      const res = await axios.get(`${API}/chat/sessions/${sessionId}`);
      setConvo(res.data);
      await axios.post(`${API}/chat/sessions/${sessionId}/mark-read`);
      // Reflect read status locally
      setSessions((arr) => arr.map((s) => s.id === sessionId ? { ...s, unread_for_dispatcher: false } : s));
    } catch (e) {
      setConvo({ error: e?.response?.data?.detail || 'Failed to load conversation' });
    }
  };

  // Poll the open conversation for new messages
  useEffect(() => {
    if (!activeId) return;
    const id = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/chat/sessions/${activeId}`);
        setConvo(res.data);
      } catch (err) { console.warn('[live-chats] convo poll failed:', err); }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [activeId]);

  const takeOver = async () => {
    if (!activeId) return;
    await axios.post(`${API}/chat/sessions/${activeId}/take-over`);
    openSession(activeId);
    refreshSessions();
  };

  const release = async () => {
    if (!activeId) return;
    await axios.post(`${API}/chat/sessions/${activeId}/release`);
    openSession(activeId);
    refreshSessions();
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending || !activeId) return;
    setSending(true);
    setInput('');
    try {
      await axios.post(`${API}/chat/sessions/${activeId}/send`, { text });
      await openSession(activeId);
      refreshSessions();
    } finally {
      setSending(false);
    }
  };

  const sess = convo?.session;
  const unreadCount = sessions.filter((s) => s.unread_for_dispatcher).length;

  return (
    <div className="space-y-3" data-testid="live-chats-tab">
      <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <MessageCircle className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-black uppercase tracking-wide">Live Chats</h2>
            {unreadCount > 0 && (
              <span className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" data-testid="unread-pill">
                {unreadCount} new
              </span>
            )}
          </div>
          <p className="text-zinc-400 text-xs">
            Carriers chatting with the AI on the load board. Take over any conversation to reply directly.
          </p>
        </div>
        <button
          onClick={refreshSessions}
          className="text-zinc-400 hover:text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
          data-testid="refresh-chats"
        >
          <RefreshCw className="w-3 h-3" />Refresh
        </button>
      </div>

      <div className="flex border border-zinc-200 bg-white w-fit">
        <button
          onClick={() => setFilterAll(true)}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${filterAll ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}
          data-testid="filter-all-chats"
        >
          All Chats
        </button>
        <button
          onClick={() => setFilterAll(false)}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors border-l border-zinc-200 ${!filterAll ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}
          data-testid="filter-my-chats"
        >
          Mine Only
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 min-h-[560px]">
        {/* Session list */}
        <div className="bg-white border border-zinc-200 overflow-hidden md:col-span-1">
          <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Sessions ({sessions.length})</span>
          </div>
          {sessions.length === 0 ? (
            <div className="py-16 text-center text-sm text-zinc-500">No active chats yet.</div>
          ) : (
            <div className="overflow-y-auto max-h-[560px] divide-y divide-zinc-100" data-testid="sessions-list">
              {sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openSession(s.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors ${activeId === s.id ? 'bg-red-50 border-l-4 border-red-600' : ''}`}
                  data-testid={`session-row-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-sm font-bold text-zinc-900 truncate">
                      {s.carrier_name || 'Anonymous Carrier'}
                    </span>
                    <span className="text-[10px] text-zinc-400 ml-2">{formatRelative(s.last_message_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mb-1">
                    {s.status === 'human' ? (
                      <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 font-bold uppercase flex items-center gap-1">
                        <Hand className="w-2.5 h-2.5" />Human
                      </span>
                    ) : (
                      <span className="text-[9px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 font-bold uppercase flex items-center gap-1">
                        <Bot className="w-2.5 h-2.5" />AI
                      </span>
                    )}
                    {s.carrier_mc && <span className="text-[10px] text-zinc-500">MC #{s.carrier_mc}</span>}
                    {s.unread_for_dispatcher && <span className="w-2 h-2 bg-red-600 rounded-full ml-auto" data-testid="unread-dot" />}
                  </div>
                  {s.last_preview && (
                    <div className="text-[11px] text-zinc-500 truncate">
                      {s.last_role === 'user' ? '→ ' : ''}
                      {s.last_preview}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Conversation pane */}
        <div className="bg-white border border-zinc-200 overflow-hidden md:col-span-2 flex flex-col">
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 py-20">
              Pick a chat session on the left to view the conversation.
            </div>
          ) : !convo ? (
            <div className="flex-1 flex items-center justify-center"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>
          ) : convo.error ? (
            <div className="p-5 text-sm text-red-700">{convo.error}</div>
          ) : (
            <>
              {/* Conversation header */}
              <div className="border-b border-zinc-200 px-4 py-3 bg-zinc-50 flex items-center justify-between gap-3 flex-wrap" data-testid="convo-header">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-bold text-zinc-900 text-sm truncate">
                      {sess.carrier_name || 'Anonymous Carrier'}
                    </span>
                    {sess.status === 'human' ? (
                      <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 font-bold uppercase flex items-center gap-1">
                        <Hand className="w-2.5 h-2.5" />{sess.assigned_to_name || 'Human'}
                      </span>
                    ) : (
                      <span className="text-[9px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 font-bold uppercase flex items-center gap-1">
                        <Bot className="w-2.5 h-2.5" />AI
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-3 flex-wrap">
                    {sess.carrier_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{sess.carrier_phone}</span>}
                    {sess.carrier_mc && <span>MC #{sess.carrier_mc}</span>}
                    {sess.load_ref && <span>· Load #{sess.load_ref}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {sess.status === 'ai' ? (
                    <button
                      onClick={takeOver}
                      className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 flex items-center gap-1"
                      data-testid="take-over-btn"
                    >
                      <Hand className="w-3 h-3" />Take Over
                    </button>
                  ) : (
                    <button
                      onClick={release}
                      className="bg-zinc-200 hover:bg-zinc-300 text-zinc-900 text-xs font-bold uppercase tracking-wider px-3 py-2"
                      data-testid="release-btn"
                      title="Hand the chat back to the AI"
                    >
                      Release to AI
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-zinc-50" data-testid="convo-messages">
                {(convo.messages || []).map((m, i) => (
                  <ConvoMessage key={m.id || i} m={m} />
                ))}
              </div>

              {/* Composer */}
              <div className="border-t border-zinc-200 p-2 flex gap-2 bg-white">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                  placeholder={sess.status === 'human' ? 'Reply to carrier…' : 'Type to take over and reply…'}
                  disabled={sending}
                  className="flex-1 bg-zinc-50 border border-zinc-200 text-sm px-3 py-2 focus:outline-none focus:border-red-600 disabled:opacity-60"
                  data-testid="convo-input"
                />
                <QuickRepliesPopover
                  onInsert={(text) => setInput((prev) => (prev ? `${prev} ${text}` : text))}
                  context={{
                    carrier_name: sess.carrier_name || 'Driver',
                    load_ref: sess.load_ref || '',
                  }}
                  currentUserName={user?.name || (user?.email || '').split('@')[0]}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-zinc-300 text-white px-4 flex items-center justify-center"
                  data-testid="convo-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ConvoMessage = ({ m }) => {
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-0.5 text-right">Carrier</div>
          <div className="px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed bg-zinc-900 text-white">{m.text}</div>
        </div>
      </div>
    );
  }
  if (m.role === 'assistant') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[78%]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-0.5 flex items-center gap-1"><Bot className="w-2.5 h-2.5" />AI</div>
          <div className="px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed bg-white border border-zinc-200 text-zinc-900">{m.text}</div>
        </div>
      </div>
    );
  }
  if (m.role === 'dispatcher') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[78%]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-green-700 mb-0.5 flex items-center gap-1">
            <User className="w-2.5 h-2.5" />{m.sender_name || 'Dispatcher'}
          </div>
          <div className="px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed bg-green-50 border border-green-200 text-zinc-900">{m.text}</div>
        </div>
      </div>
    );
  }
  return null;
};

// ===== helpers =====
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    try { await Notification.requestPermission(); }
    catch (err) { console.debug('[live-chats] notification permission request blocked:', err); }
  }
};

const fireNotification = (session) => {
  const name = session.carrier_name || 'Anonymous Carrier';
  const preview = session.last_preview || 'New message';
  // Browser notification
  try {
    if ('Notification' in window && Notification.permission === 'granted') {
      const n = new Notification(`💬 ${name}`, { body: preview, tag: session.id });
      setTimeout(() => n.close(), 6000);
    }
  } catch (err) { console.debug('[live-chats] notification fire blocked:', err); }
  // Audio chime
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.05, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      o.start();
      o.stop(ctx.currentTime + 0.25);
    }
  } catch (err) { console.debug('[live-chats] audio chime blocked:', err); }
};

export default LiveChatsTab;
